/**
 * Finance V2 — Repayment + Loss Curves.
 */
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { formatApiError } from "../../lib/errors";
import { Section, CategoryChip, fmtPct } from "./_shared";

export default function FinanceCurves() {
  const [source, setSource] = useState("modeled");
  const [scenario, setScenario] = useState("base");
  const [repayment, setRepayment] = useState(null);
  const [loss, setLoss] = useState(null);

  const load = async () => {
    try {
      const [{ data: rc }, { data: lc }] = await Promise.all([
        api.get("/finance/v2/curves/repayment", { params: { source, scenario, months: 36 } }),
        api.get("/finance/v2/curves/loss", { params: { source, scenario, months: 36 } }),
      ]);
      setRepayment(rc); setLoss(lc);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [source, scenario]);

  return (
    <div className="p-6 max-w-[1200px]" data-testid="finance-curves-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0A2540]">Repayment & Loss Curves</h1>
          <div className="text-xs text-[#6B7280] mt-1">
            Period + cumulative curves through month 36. Historical curves are labeled Forecast unless the sample-size floor is met.
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#6B7280] block mb-1">Source</label>
          <select value={source} onChange={(e) => setSource(e.target.value)} className="h-9 px-3 text-sm border border-[#E5E7EB] rounded-md bg-white" data-testid="source-select">
            <option value="modeled">Modeled / Forecast</option>
            <option value="historical">Historical (from DB)</option>
          </select>
        </div>
        {source === "modeled" && (
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#6B7280] block mb-1">Scenario</label>
            <select value={scenario} onChange={(e) => setScenario(e.target.value)} className="h-9 px-3 text-sm border border-[#E5E7EB] rounded-md bg-white" data-testid="scenario-select">
              <option value="base">Base</option><option value="upside">Upside</option><option value="downside">Downside</option>
            </select>
          </div>
        )}
      </div>

      {repayment && (
        <Section title="Repayment / Completion Curve" category={repayment.category}
                  subtitle={repayment.caveats?.join(" · ") || ""}>
          <CurveTable rows={repayment.rows} periodKey="period_resolved_pct" cumKey="cumulative_resolved_pct"
                       periodLabel="% Resolved" cumLabel="Cumulative %" showRemaining />
        </Section>
      )}
      {loss && (
        <Section title="Loss Curve" category={loss.category}
                  subtitle={loss.caveats?.join(" · ") || ""}>
          <CurveTable rows={loss.rows} periodKey="period_loss_pct" cumKey="cumulative_loss_pct"
                       periodLabel="Period Loss %" cumLabel="Cumulative Loss %" />
        </Section>
      )}
    </div>
  );
}

const CurveTable = ({ rows = [], periodKey, cumKey, periodLabel, cumLabel, showRemaining }) => {
  const max = Math.max(...rows.map(r => r[periodKey] || 0), 0.0001);
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B7280] border-b border-[#E5E7EB]">
          <th className="py-2">Month</th>
          <th className="text-right">{periodLabel}</th>
          <th></th>
          <th className="text-right">{cumLabel}</th>
          {showRemaining && <th className="text-right">Remaining %</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.month_since_funding} className="border-b border-[#F3F4F6]" data-testid={`curve-row-${r.month_since_funding}`}>
            <td className="py-1.5">M{r.month_since_funding}</td>
            <td className="tabular-nums text-right w-24">{fmtPct(r[periodKey] || 0)}</td>
            <td className="w-64 pr-4">
              <div className="h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
                <div className="h-full bg-[#0066FF] rounded-full" style={{ width: `${((r[periodKey] || 0) / max) * 100}%` }} />
              </div>
            </td>
            <td className="tabular-nums text-right w-24">{fmtPct(r[cumKey] || 0)}</td>
            {showRemaining && <td className="tabular-nums text-right w-24">{fmtPct(r.remaining_unresolved_pct || 0)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
